import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { v4 as uuidv4 } from 'uuid';
import AtlasSdk from '@atlas/sdk';
import { useAppDispatch } from '@/store';
import { setSseUuid } from '@/slices/global';
import { DynamicUrl, SseConfig, StaticUrl } from '../constant';
import { convertArgs } from '../http';
import { convertURLSearchParams } from '../util';

type SseRuntimeConfigType<T> = {
  onopen?: () => void;
  onmessage?: (event: T) => void;
  onerror?: (errorMessage: string) => void;
};

export class SseRuntimeConfig<T> {
  constructor(config?: SseRuntimeConfigType<T>) {
    if (!config) return;
    if (config.onmessage) this.onmessage = config.onmessage;
    if (config.onopen) this.onopen = config.onopen;
    if (config.onerror) this.onerror = config.onerror;
  }
  static create<T>(config?: SseRuntimeConfigType<T>) {
    return new SseRuntimeConfig(config);
  }
  onopen?: () => void;
  onmessage?: (data: T) => void;
  onerror?: (errorMessage: string) => void;

  private __proto__: object = SseRuntimeConfig.prototype;
}

export interface Run<T> {
  (): void;
  (sseRuntimeConfig?: SseRuntimeConfig<T>): void;
}
export interface RunWithPayload<T, P> {
  (payload: P): void;
  (payload: P, sseRuntimeConfig?: SseRuntimeConfig<T>): void;
}
export interface RunWithUrlArg<T> {
  (urlArg: string | number): void;
  (urlArg: string | number, sseRuntimeConfig?: SseRuntimeConfig<T>): void;
}
export interface RunWithUrlArgAndPayload<T, P> {
  (urlArg: string | number, payload: P): void;
  (urlArg: string | number, payload: P, sseRuntimeConfig?: SseRuntimeConfig<T>): void;
}

export type RunWith<T, P> = Run<T> | RunWithPayload<T, P> | RunWithUrlArg<T> | RunWithUrlArgAndPayload<T, P>;

export interface State<T> {
  status: 'Init' | 'Loading' | 'Messaging' | 'Closed';
  error: Error | null;
  data: T | null;
}
const initialState: State<any> = {
  status: 'Init',
  data: null,
  error: null,
};

type RequestState<T, P, R extends RunWith<T, P>> = State<T> & {
  cancel: () => void;
  run: R;
};

let hasConnected = false;
const uuid = uuidv4();
const MAX_RETRIES = 10;
const BASE_DELAY = 1000;

function useEventSource<T = null>(config: SseConfig<StaticUrl>): RequestState<T, void, Run<T>>;
function useEventSource<T = null, P = undefined>(
  config: SseConfig<StaticUrl>,
): RequestState<T, P, RunWithPayload<T, P>>;
function useEventSource<T = null>(config: SseConfig<DynamicUrl>): RequestState<T, void, RunWithUrlArg<T>>;
function useEventSource<T = null, P = undefined>(
  config: SseConfig<DynamicUrl>,
): RequestState<T, P, RunWithUrlArgAndPayload<T, P>>;
function useEventSource<T>(config: SseConfig<any>) {
  const dispatch = useAppDispatch();
  const [state, setState] = useState<State<T>>({ ...initialState });
  const abortControllerRef = useRef<AbortController>();
  const timerIdRef = useRef<NodeJS.Timeout | null>(null);

  const wrappedRun = useCallback(
    async (arg_0?: any, arg_1?: any, arg_2?: any) => {
      if (hasConnected) return;

      setState((prev) => ({ ...prev, status: 'Loading', error: null }));

      let retryCount = 0;

      const { url, payload, runtimeConfig } = convertArgs(config, arg_0, arg_1, arg_2);
      const params = convertURLSearchParams(payload);
      const sseUrl = params ? url + `?${params.toString()}` : url;
      const sseRuntimeConfig = runtimeConfig ? (runtimeConfig as SseRuntimeConfig<T>) : undefined;

      const { apiGatewayUrl } = await AtlasSdk.environment.getConfig();
      const runtimeUrl = sseUrl.startsWith('http') ? sseUrl : apiGatewayUrl + sseUrl;

      const tryReconnect = () => {
        if (retryCount < MAX_RETRIES) {
          timerIdRef.current && clearTimeout(timerIdRef.current);
          retryCount++;
          const delay = BASE_DELAY * Math.pow(2, retryCount - 1);
          timerIdRef.current = setTimeout(handleFetchEventSource, delay);
          return true;
        }
        return false;
      };

      const handleFetchEventSource = async () => {
        try {
          const accessToken = await AtlasSdk.authorization.getAccessToken();
          const headers = { ...config.headers, Authorization: `Bearer ${accessToken}`, 'X-CASE-WATCHING-X': uuid };

          abortControllerRef.current?.abort();
          abortControllerRef.current = new AbortController();
          await fetchEventSource(runtimeUrl, {
            method: 'GET',
            headers,
            openWhenHidden: true,
            signal: abortControllerRef.current.signal,
            async onopen(response) {
              if (!response.ok) {
                throw new Error(`SSE connection failed with status ${response.status}`);
              }

              sseRuntimeConfig?.onopen?.();
              dispatch(setSseUuid(uuid));
              hasConnected = true;
              retryCount = 0;
            },
            onmessage(event) {
              const data = JSON.parse(event.data);
              if (!data) return;
              sseRuntimeConfig?.onmessage?.(data);
              setState((prev) => ({ ...prev, status: 'Messaging', data }));
            },
            onerror(error) {
              const errorMsg = error.message || error.data;
              sseRuntimeConfig?.onerror?.(errorMsg);
              setState((prev) => ({ ...prev, error: new Error(errorMsg) }));
              dispatch(setSseUuid(''));
              hasConnected = false;
              tryReconnect();
              throw new Error(errorMsg);
            },
          });
        } catch (error: any) {
          setState((prev) => ({ ...prev, error }));
        }
      };

      handleFetchEventSource();
    },
    [config, dispatch],
  );

  const wrappedCancel = useCallback(() => {
    setState((prev) => ({ ...prev, status: 'Closed' }));
    abortControllerRef.current?.abort();
    abortControllerRef.current = undefined;
    hasConnected = false;
  }, []);

  useEffect(() => {
    return () => {
      timerIdRef.current && clearTimeout(timerIdRef.current);
    };
  }, []);

  return {
    ...state,
    cancel: wrappedCancel,
    run: wrappedRun,
  };
}

export default useEventSource;
